import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { API, safeJson } from "../lib/api";
import {
  trackSignUp,
  trackLogin,
  trackLessonComplete,
  identifyUser,
} from "../lib/analytics";

// API now resolved at runtime via /lib/api.js
const TOKEN_KEY = "rebel_jwt";
const REF_KEY = "rebel_ref";

// Capture ?ref= from URL on first load and persist for 30 days.
const captureReferralFromUrl = () => {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref && /^[A-Z0-9]{6,12}$/i.test(ref)) {
      localStorage.setItem(REF_KEY, ref.toUpperCase());
      localStorage.setItem(`${REF_KEY}_at`, String(Date.now()));
    }
  } catch {}
};

const getStoredReferral = () => {
  try {
    const ref = localStorage.getItem(REF_KEY);
    const at = parseInt(localStorage.getItem(`${REF_KEY}_at`) || "0", 10);
    if (!ref) return null;
    // Expire after 30 days
    if (at && Date.now() - at > 30 * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(REF_KEY);
      localStorage.removeItem(`${REF_KEY}_at`);
      return null;
    }
    return ref;
  } catch { return null; }
};

captureReferralFromUrl();

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null=loading, false=guest, {…}=authenticated
  const [progress, setProgress] = useState({ completed_keys: [], completed_count: 0, total: 51, all_completed: false });
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
  });
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login"); // 'login' | 'register'

  const persistToken = (t) => {
    setToken(t);
    try {
      if (t) localStorage.setItem(TOKEN_KEY, t);
      else localStorage.removeItem(TOKEN_KEY);
    } catch {}
  };

  const fetchMe = useCallback(async (t) => {
    try {
      const res = await fetch(`${API}/auth/me`, {
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error("unauth");
      const data = await safeJson(res);
      setUser(data);
      // GA4 identify (no PII — Mongo id only)
      if (data?.id) identifyUser(data.id, { isPro: !!data.is_pro });
      // also fetch progress
      try {
        const pr = await fetch(`${API}/lessons/progress`, {
          credentials: "include",
          headers: t ? { Authorization: `Bearer ${t}` } : {},
        });
        if (pr.ok) setProgress(await safeJson(pr));
      } catch {}
      return data;
    } catch {
      setUser(false);
      setProgress({ completed_keys: [], completed_count: 0, total: 51, all_completed: false });
      return null;
    }
  }, []);

  const completeLesson = useCallback(async (lessonKey, score, total = 3, answers = null) => {
    const res = await fetch(`${API}/lessons/${encodeURIComponent(lessonKey)}/complete`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ score, total, answers }),
    });
    const data = await safeJson(res);
    if (!res.ok) throw new Error(formatErr(data?.detail) || "Complete failed");
    if (data.progress) setProgress((p) => ({ ...p, ...data.progress }));
    // GA4 — lesson_complete event
    try {
      trackLessonComplete({
        lessonKey,
        score,
        total,
        allCompleted: !!data.progress?.all_completed,
      });
    } catch {}
    return data;
  }, [token]);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /me check.
    // GoogleAuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      // Leave user as null (loading) — callback will set it after exchange
      return;
    }
    fetchMe(token);
  }, [fetchMe, token]);

  const register = async ({ email, password, name }) => {
    const referral_code = getStoredReferral();
    const res = await fetch(`${API}/auth/register`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, name, referral_code }),
    });
    const data = await safeJson(res);
    if (!res.ok) throw new Error(formatErr(data?.detail) || "Register failed");
    persistToken(data.token);
    setUser(data.user);
    try { trackSignUp("email"); identifyUser(data.user?.id, { isPro: !!data.user?.is_pro }); } catch {}
    return data.user;
  };

  const login = async ({ email, password }) => {
    const res = await fetch(`${API}/auth/login`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await safeJson(res);
    if (!res.ok) throw new Error(formatErr(data?.detail) || "Login failed");
    persistToken(data.token);
    setUser(data.user);
    try { trackLogin("email"); identifyUser(data.user?.id, { isPro: !!data.user?.is_pro }); } catch {}
    return data.user;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } catch {}
    persistToken(null);
    setUser(false);
  };

  // Kick off Google OAuth (Emergent-managed)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  };

  // Exchange Emergent session_id (from URL hash after Google redirect) for our JWT
  const exchangeGoogleSession = async (sessionId) => {
    const referral_code = getStoredReferral();
    const res = await fetch(`${API}/auth/google/exchange`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId, referral_code }),
    });
    const data = await safeJson(res);
    if (!res.ok) throw new Error(formatErr(data?.detail) || "Google login failed");
    persistToken(data.token);
    setUser(data.user);
    try { trackLogin("google"); identifyUser(data.user?.id, { isPro: !!data.user?.is_pro }); } catch {}
    return data.user;
  };

  const authHeaders = () => (token ? { Authorization: `Bearer ${token}` } : {});

  const refreshUser = () => fetchMe(token);

  const openLogin = () => { setAuthMode("login"); setAuthOpen(true); };
  const openRegister = () => { setAuthMode("register"); setAuthOpen(true); };

  return (
    <AuthContext.Provider value={{
      user, token, isPro: !!user?.is_pro,
      progress, trialExpired: !user?.is_pro && progress.all_completed,
      trial: user?.trial || { tts_remaining: 1, chat_remaining: 1, tts_used: 0, chat_used: 0 },
      completeLesson,
      register, login, logout, loginWithGoogle, exchangeGoogleSession,
      authHeaders, refreshUser,
      authOpen, setAuthOpen, authMode, setAuthMode, openLogin, openRegister,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

function formatErr(d) {
  if (d == null) return null;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (d?.msg) return d.msg;
  return String(d);
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};
